import { useState, useRef, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Crop, RotateCw, ZoomIn, ZoomOut, Check } from "lucide-react";

export function ImageCropper({ imageBase64, onCrop, onCancel, open }) {
  const canvasRef = useRef(null);
  const [cropArea, setCropArea] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  const drawImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageBase64) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      const maxW = 600, maxH = 400;
      let w = img.width, h = img.height;
      if (w > maxW) { h = h * maxW / w; w = maxW; }
      if (h > maxH) { w = w * maxH / h; h = maxH; }
      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      if (rotation) {
        ctx.translate(w / 2, h / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-w / 2, -h / 2);
      }
      ctx.drawImage(img, 0, 0, w, h);
      ctx.restore();
      setImgSize({ w, h });
      setCropArea(null);
    };
    img.src = `data:image/jpeg;base64,${imageBase64}`;
  }, [imageBase64, rotation]);

  useEffect(() => { if (open) drawImage(); }, [open, drawImage]);

  const drawOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !cropArea) return;
    drawImage();
    setTimeout(() => {
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(cropArea.x, cropArea.y, cropArea.w, cropArea.h);
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(cropArea.x, cropArea.y, cropArea.w, cropArea.h);
    }, 50);
  }, [cropArea, drawImage]);

  useEffect(() => { if (cropArea) drawOverlay(); }, [cropArea, drawOverlay]);

  const getCanvasPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const handleStart = (e) => {
    e.preventDefault();
    const pos = getCanvasPos(e);
    setDragStart(pos);
    setDragging(true);
    setCropArea(null);
  };

  const handleMove = (e) => {
    if (!dragging || !dragStart) return;
    e.preventDefault();
    const pos = getCanvasPos(e);
    const x = Math.min(dragStart.x, pos.x);
    const y = Math.min(dragStart.y, pos.y);
    const w = Math.abs(pos.x - dragStart.x);
    const h = Math.abs(pos.y - dragStart.y);
    if (w > 10 && h > 10) setCropArea({ x, y, w, h });
  };

  const handleEnd = () => setDragging(false);

  const handleCrop = () => {
    if (!cropArea || !canvasRef.current) {
      onCrop(imageBase64);
      return;
    }
    const srcImg = new Image();
    srcImg.onload = () => {
      const canvas = canvasRef.current;
      const scaleX = srcImg.width / canvas.width;
      const scaleY = srcImg.height / canvas.height;
      const outCanvas = document.createElement("canvas");
      const cw = cropArea.w * scaleX;
      const ch = cropArea.h * scaleY;
      outCanvas.width = cw;
      outCanvas.height = ch;
      const ctx = outCanvas.getContext("2d");
      ctx.drawImage(srcImg, cropArea.x * scaleX, cropArea.y * scaleY, cw, ch, 0, 0, cw, ch);
      const result = outCanvas.toDataURL("image/jpeg", 0.9).split(",")[1];
      onCrop(result);
    };
    srcImg.src = `data:image/jpeg;base64,${imageBase64}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crop className="w-5 h-5 text-blue-500" />
            Crop Image
          </DialogTitle>
          <DialogDescription>
            Drag on the image to select the area you want to keep. This helps the AI focus on the relevant part.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            className="border rounded-lg cursor-crosshair max-w-full"
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setRotation((r) => (r + 90) % 360)}>
              <RotateCw className="w-4 h-4 mr-1" /> Rotate
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setCropArea(null); drawImage(); }}>
              Reset
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {cropArea ? `Selected: ${Math.round(cropArea.w)}×${Math.round(cropArea.h)}px` : "Drag to select crop area"}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleCrop}>
            <Check className="w-4 h-4 mr-2" />
            {cropArea ? "Apply Crop" : "Use Full Image"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
